import { Toast, Icon, Box, Link } from "@/components";
import { useState } from "react";
import { useBlockExplorer } from "@/hooks";
import type { GenericTransactionFailedPayload } from "@/events";
import type { Hash } from "viem";

interface TransactionFailedToastProps extends GenericTransactionFailedPayload {
    error: Error | string;
    context?: string;
    txHash?: Hash;
    onClose?: () => void;
}

export function TransactionFailedToast({ error, context, txHash, onClose }: TransactionFailedToastProps) {
    const [open, setOpen] = useState(true);
    const { getTransactionUrl } = useBlockExplorer();

    const errorMessage = typeof error === "string" ? error : error?.message || "Unknown error";

    // Handle different failure scenarios with appropriate titles
    let title = "Transaction failed";
    if (!txHash) {
        // No transaction hash indicates failure before transaction was sent
        title = "Transaction failed to send";
    } else if (context) {
        title = `Error ${context}`;
    }

    // Only generate URL if we have a transaction hash
    const blockExplorerUrl = txHash ? getTransactionUrl(txHash) : undefined;

    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const body = (
        <Box className="flex flex-col gap-2 px-4 mb-2">
            <div className="text-red-400">{errorMessage}</div>
            {blockExplorerUrl && (
                <Link
                    href={blockExplorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-400 hover:text-red-300 flex items-center gap-1 cursor-pointer"
                >
                    View Transaction
                    <span className="text-lg">↗</span>
                </Link>
            )}
        </Box>
    );

    return (
        <Toast
            open={open}
            onClose={handleClose}
            title={title}
            icon={<Icon name="Error" className="h-6 w-6 relative top-[2px] text-red-500" />}
            severity="error"
        >
            <>
                <div className="h-px w-full bg-red-800/30" />
                {body}
            </>
        </Toast>
    );
}
